import { kml } from '@tmcw/togeojson'
import type { Feature, FeatureCollection } from 'geojson'
import { unzip, type ZipEntry } from 'unzipit'
import { v4 as uuid } from 'uuid'

import {
  ConvertedGeometryCode,
  ConvertedGeometryData,
  ConvertedGeometrySourceFormat,
  ConvertGeometryControl,
  ConvertGeometryDiagnostics,
  ConvertGeometryStage,
  ConvertGeometryStats,
  MAX_KML_ENTRIES_PER_KMZ,
  USER_GEOMETRY_MAX_FILE_BYTES
} from '@/domain/protocols/UserGeometry'

import { serializeFeatureCollectionToFlatGeobuf } from './userGeometryFlatGeobuf'
import { optimizeGeoJsonForStorage } from './userGeometryGeoJsonNormalization'
import { computeGeoJsonBounds } from './userGeometryGeoJsonRender'

// Conversion outcome. `diagnostics.code` always explains what happened; `data`
// (the produced .fgb payload) is present only when `success` is true. A flat
// shape is used instead of a discriminated union because the project compiles
// with `strictNullChecks: false`, where union narrowing is unreliable.
export interface ConvertGeometryResult {
  success: boolean
  diagnostics: ConvertGeometryDiagnostics
  data?: ConvertedGeometryData
}

export interface ConvertGeometryOptions extends ConvertGeometryControl {
  // Stable id shared between prepare & save; auto-generated when omitted.
  geometryId?: string
}

export type ConvertGeometryBinarySource = ArrayBuffer | ArrayBufferView
export type ConvertGeometryKmlSource = string | ConvertGeometryBinarySource

// Monotonic progress anchors per stage; PARSING_KML ramps between its anchor and
// NORMALIZING as KML entries are processed (KMZ), so the bar advances per entry.
const STAGE_PROGRESS: Record<ConvertGeometryStage, number> = {
  PREPARING_INPUT: 0,
  UNZIPPING: 0.1,
  PARSING_KML: 0.25,
  NORMALIZING: 0.8,
  SERIALIZING: 0.9,
  DONE: 1
}

const toUint8Array = (source: ConvertGeometryBinarySource): Uint8Array => {
  if (source instanceof Uint8Array) {
    return source
  }
  if (source instanceof ArrayBuffer) {
    return new Uint8Array(source)
  }
  return new Uint8Array(source.buffer, source.byteOffset, source.byteLength)
}

const decodeKmlSourceToText = (source: ConvertGeometryKmlSource): string => {
  if (typeof source === 'string') {
    return source
  }
  return new TextDecoder('utf-8').decode(toUint8Array(source))
}

const isCancelled = (control?: ConvertGeometryControl): boolean => control?.isCancelled?.() === true

const reportProgress = (
  control: ConvertGeometryControl | undefined,
  stage: ConvertGeometryStage,
  progress: number,
  entries?: { processedKmlEntries?: number; totalKmlEntries?: number }
): void => {
  control?.onProgress?.({
    stage,
    progress: Math.max(0, Math.min(1, progress)),
    ...entries
  })
}

const failure = (
  sourceFormat: ConvertedGeometrySourceFormat,
  code: ConvertedGeometryCode,
  stats: ConvertGeometryStats,
  warnings: string[] = []
): ConvertGeometryResult => ({
  success: false,
  diagnostics: { sourceFormat, code, warnings, stats }
})

const cancelled = (sourceFormat: ConvertedGeometrySourceFormat): ConvertGeometryResult =>
  failure(sourceFormat, 'CANCELLED', { featureCount: 0 })

// Parses XML text into a DOM Document, returning null when the parser reports a
// `<parsererror>` element (the standard browser/jsdom malformed-XML signal).
const parseXmlDocument = (text: string): Document | null => {
  const doc = new DOMParser().parseFromString(text, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length > 0) {
    return null
  }
  return doc
}

// Converts a single KML document text to features, returning null on any parse
// failure (malformed XML or a throw from the KML reader).
const parseKmlTextToFeatures = (text: string): Feature[] | null => {
  const doc = parseXmlDocument(text)
  if (!doc) {
    return null
  }
  try {
    const collection = kml(doc) as FeatureCollection
    return Array.isArray(collection.features) ? collection.features : []
  } catch {
    return null
  }
}

const isKmlEntryName = (name: string): boolean => name.toLowerCase().endsWith('.kml')

const getEntryBaseName = (name: string): string => {
  const normalized = name.replace(/\\/g, '/')
  const segments = normalized.split('/')
  return segments[segments.length - 1] ?? normalized
}

// Guards against path traversal / absolute paths inside the archive. In-memory
// unzip already isolates entries, but the names are still validated defensively.
const isUnsafeEntryPath = (name: string): boolean => {
  const normalized = name.replace(/\\/g, '/')
  if (normalized.startsWith('/')) {
    return true
  }
  if (/^[a-zA-Z]:\//.test(normalized)) {
    return true
  }
  return normalized.split('/').some((segment) => segment === '..')
}

// Orders KML entries so any `doc.kml` (the conventional main entry) is parsed
// first, preserving the original order of the remaining entries.
const orderKmlEntriesDocFirst = (entries: ZipEntry[]): ZipEntry[] => {
  const isDocKml = (entry: ZipEntry): boolean => getEntryBaseName(entry.name).toLowerCase() === 'doc.kml'
  return [...entries.filter(isDocKml), ...entries.filter((entry) => !isDocKml(entry))]
}

// Shared tail of the pipeline: normalize the merged features, serialize to .fgb,
// frame the bounds and assemble the successful payload (or a no-features failure).
const finalizeFeatures = (params: {
  features: Feature[]
  sourceFormat: ConvertedGeometrySourceFormat
  control: ConvertGeometryControl | undefined
  geometryId: string
  warnings: string[]
  code: ConvertedGeometryCode
  stats: ConvertGeometryStats
}): ConvertGeometryResult => {
  const { features, sourceFormat, control, geometryId, warnings, code, stats } = params

  if (isCancelled(control)) {
    return cancelled(sourceFormat)
  }
  reportProgress(control, 'NORMALIZING', STAGE_PROGRESS.NORMALIZING)
  const optimized = optimizeGeoJsonForStorage({ type: 'FeatureCollection', features })
  const featureCount = optimized.features.length

  if (featureCount === 0) {
    return failure(sourceFormat, 'KMZ_NO_VALID_FEATURES', { ...stats, featureCount: 0 }, warnings)
  }

  if (isCancelled(control)) {
    return cancelled(sourceFormat)
  }
  reportProgress(control, 'SERIALIZING', STAGE_PROGRESS.SERIALIZING)

  let fgbBytes: Uint8Array
  try {
    fgbBytes = serializeFeatureCollectionToFlatGeobuf(optimized)
  } catch {
    return failure(sourceFormat, 'UNEXPECTED_ERROR', { ...stats, featureCount }, warnings)
  }

  const bounds = computeGeoJsonBounds(optimized)
  if (!bounds) {
    return failure(sourceFormat, 'KMZ_NO_VALID_FEATURES', { ...stats, featureCount }, warnings)
  }

  reportProgress(control, 'DONE', STAGE_PROGRESS.DONE)

  const diagnostics: ConvertGeometryDiagnostics = {
    sourceFormat,
    code,
    warnings,
    stats: { ...stats, featureCount }
  }

  return {
    success: true,
    diagnostics,
    data: {
      geometryId,
      fgbBytes,
      bounds: { sw: bounds.sw, ne: bounds.ne },
      featureCount,
      diagnostics
    }
  }
}

// Converts a single `.kml` source (text or bytes) to FlatGeobuf bytes.
export const convertKMLToFlatGeobuf = async (
  source: ConvertGeometryKmlSource,
  options?: ConvertGeometryOptions
): Promise<ConvertGeometryResult> => {
  const sourceFormat: ConvertedGeometrySourceFormat = 'kml'
  const geometryId = options?.geometryId ?? uuid()

  try {
    if (isCancelled(options)) {
      return cancelled(sourceFormat)
    }
    reportProgress(options, 'PREPARING_INPUT', STAGE_PROGRESS.PREPARING_INPUT)
    const text = decodeKmlSourceToText(source)

    if (isCancelled(options)) {
      return cancelled(sourceFormat)
    }
    reportProgress(options, 'PARSING_KML', STAGE_PROGRESS.PARSING_KML, { processedKmlEntries: 0, totalKmlEntries: 1 })
    const features = parseKmlTextToFeatures(text)
    if (features === null) {
      return failure(sourceFormat, 'KML_PARSE_FAILED', { featureCount: 0 })
    }
    reportProgress(options, 'PARSING_KML', STAGE_PROGRESS.NORMALIZING, {
      processedKmlEntries: 1,
      totalKmlEntries: 1
    })

    return finalizeFeatures({
      features,
      sourceFormat,
      control: options,
      geometryId,
      warnings: [],
      code: 'OK',
      stats: { featureCount: 0 }
    })
  } catch {
    return failure(sourceFormat, 'UNEXPECTED_ERROR', { featureCount: 0 })
  }
}

// Converts a `.kmz` archive (bytes) to FlatGeobuf bytes: unzips in memory, merges
// every `.kml` entry (doc.kml first), tolerates partial parse failures and
// enforces all size/entry guards.
export const convertKMZToFlatGeobuf = async (
  source: ConvertGeometryBinarySource,
  options?: ConvertGeometryOptions
): Promise<ConvertGeometryResult> => {
  const sourceFormat: ConvertedGeometrySourceFormat = 'kmz'
  const geometryId = options?.geometryId ?? uuid()

  try {
    if (isCancelled(options)) {
      return cancelled(sourceFormat)
    }
    reportProgress(options, 'PREPARING_INPUT', STAGE_PROGRESS.PREPARING_INPUT)
    const bytes = toUint8Array(source)
    if (bytes.byteLength > USER_GEOMETRY_MAX_FILE_BYTES) {
      return failure(sourceFormat, 'KMZ_FILE_TOO_LARGE', { featureCount: 0 })
    }

    if (isCancelled(options)) {
      return cancelled(sourceFormat)
    }
    reportProgress(options, 'UNZIPPING', STAGE_PROGRESS.UNZIPPING)
    let entries: Record<string, ZipEntry>
    try {
      entries = (await unzip(bytes)).entries
    } catch {
      return failure(sourceFormat, 'KMZ_UNZIP_FAILED', { featureCount: 0 })
    }

    if (Object.keys(entries).some(isUnsafeEntryPath)) {
      return failure(sourceFormat, 'KMZ_ENTRY_PATH_INVALID', { featureCount: 0 })
    }

    const allEntries = Object.values(entries)
    const kmlEntries = allEntries.filter((entry) => !entry.isDirectory && isKmlEntryName(entry.name))
    if (kmlEntries.length === 0) {
      return failure(sourceFormat, 'KMZ_NO_KML_FILES', { featureCount: 0 })
    }
    if (kmlEntries.length > MAX_KML_ENTRIES_PER_KMZ) {
      return failure(sourceFormat, 'KMZ_TOO_MANY_KML_ENTRIES', { featureCount: 0, totalKmlEntries: kmlEntries.length })
    }
    // Per-entry guard before the aggregate one: a single oversized KML shares the
    // same ceiling as the total, so reporting the more specific cause first keeps
    // KMZ_SINGLE_KML_TOO_LARGE reachable.
    if (kmlEntries.some((entry) => entry.size > USER_GEOMETRY_MAX_FILE_BYTES)) {
      return failure(sourceFormat, 'KMZ_SINGLE_KML_TOO_LARGE', { featureCount: 0, totalKmlEntries: kmlEntries.length })
    }
    const totalUnzippedBytes = allEntries.reduce((total, entry) => total + (entry.size || 0), 0)
    if (totalUnzippedBytes > USER_GEOMETRY_MAX_FILE_BYTES) {
      return failure(sourceFormat, 'KMZ_TOTAL_UNZIPPED_TOO_LARGE', { featureCount: 0 })
    }

    const orderedEntries = orderKmlEntriesDocFirst(kmlEntries)
    const totalKmlEntries = orderedEntries.length
    const warnings: string[] = []
    const mergedFeatures: Feature[] = []
    let attemptedKmlEntries = 0
    let failedKmlEntries = 0

    for (let index = 0; index < orderedEntries.length; index += 1) {
      if (isCancelled(options)) {
        return cancelled(sourceFormat)
      }

      const entry = orderedEntries[index]
      let entryFeatures: Feature[] | null = null
      try {
        entryFeatures = parseKmlTextToFeatures(await entry.text())
      } catch {
        entryFeatures = null
      }

      if (entryFeatures === null) {
        failedKmlEntries += 1
        warnings.push(`Failed to parse KML entry "${entry.name}"`)
      } else {
        mergedFeatures.push(...entryFeatures)
      }

      attemptedKmlEntries += 1
      const parseSpan = STAGE_PROGRESS.NORMALIZING - STAGE_PROGRESS.PARSING_KML
      reportProgress(
        options,
        'PARSING_KML',
        STAGE_PROGRESS.PARSING_KML + parseSpan * (attemptedKmlEntries / totalKmlEntries),
        {
          processedKmlEntries: attemptedKmlEntries,
          totalKmlEntries
        }
      )
    }

    const stats: ConvertGeometryStats = {
      featureCount: 0,
      totalKmlEntries,
      processedKmlEntries: attemptedKmlEntries - failedKmlEntries,
      failedKmlEntries
    }

    if (mergedFeatures.length === 0) {
      return failure(sourceFormat, 'KMZ_NO_VALID_FEATURES', stats, warnings)
    }

    // Partial failures are non-fatal: surface them via a warning code while still
    // producing the geometry from the entries that parsed successfully.
    const code: ConvertedGeometryCode = failedKmlEntries > 0 ? 'KMZ_PARTIAL_KML_PARSE_FAILURE' : 'OK'

    return finalizeFeatures({
      features: mergedFeatures,
      sourceFormat,
      control: options,
      geometryId,
      warnings,
      code,
      stats
    })
  } catch {
    return failure(sourceFormat, 'UNEXPECTED_ERROR', { featureCount: 0 })
  }
}
